import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { EmptyState, PageContainer, PageHeader } from '../../components';
import { cx } from '../../lib/cx';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import { useCapability } from '../workspace/useCapability';
import { WorkspaceSection } from './sections/WorkspaceSection';
import { PeopleSection } from './sections/PeopleSection';
import { CalendarsSection } from './sections/CalendarsSection';
import { RepositoriesSection } from './sections/RepositoriesSection';

type SettingsTab = 'workspace' | 'people' | 'calendars' | 'repositories';

const TABS: readonly { readonly id: SettingsTab; readonly label: string }[] = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'people', label: 'People' },
  { id: 'calendars', label: 'Calendars' },
  { id: 'repositories', label: 'Repositories' },
];

/**
 * The configuration surface: workspace terms, people and integration
 * credentials, behind one tab switcher.
 *
 * Projects used to be a fourth tab here, which is why they felt buried: the
 * stripped-down list under Settings was the only way anyone on this side of
 * the product reached them, while the real Projects screen — parts across
 * sprints, delivery progress, changes after start — was on the client's nav
 * and not ours. The screen does everything the tab did; the tab is gone and
 * Projects is a nav item on both sides.
 *
 * `workspace.admin` gates the whole screen, which only `vendor_admin` and
 * `vendor_lead` hold (@rundown/policy). The check below is a courtesy for
 * anyone who lands here without it — direct URL entry, a stale bookmark — not
 * the actual boundary: every fetch this screen makes is refused by the API on
 * its own, same discipline as RequireCapability documents at the route layer.
 * The route-level guard belongs to routes.tsx, outside this milestone's
 * file-ownership boundary — see the integration notes handed back to the lead.
 */
export function SettingsPage() {
  const { workspace } = useRequiredWorkspace();
  const canAdminister = useCapability('workspace.admin');
  // GitHub's install callback redirects to /settings?tab=repositories, so the
  // admin lands back where they started rather than on Workspace wondering
  // whether it worked.
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const [tab, setTab] = useState<SettingsTab>(
    TABS.some((item) => item.id === requested) ? (requested as SettingsTab) : 'workspace',
  );

  if (!canAdminister) {
    return (
      <div className="mx-auto w-full max-w-[45rem] 2xl:max-w-[57.5rem] px-5 pt-9 md:px-10">
        <EmptyState title="Not available" description="Settings belongs to the delivery side of Rundown." />
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={'Settings'}
        description={
          <>
            {workspace.name} — retainer terms, people and the repositories estimates are grounded in. Every
            change here is written to the audit trail.
          </>
        }
      />

      <div className="mb-5 flex flex-wrap gap-1 border-b border-line">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            aria-current={tab === item.id}
            className={cx(
              '-mb-px rounded-t-[7px] border-b-2 px-3 py-2 fs-13 font-medium whitespace-nowrap',
              tab === item.id ? 'border-accent text-ink' : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'workspace' && <WorkspaceSection workspaceId={workspace.id} />}
      {tab === 'people' && <PeopleSection workspaceId={workspace.id} />}
      {tab === 'calendars' && <CalendarsSection workspaceId={workspace.id} />}
      {tab === 'repositories' && <RepositoriesSection workspaceId={workspace.id} />}
    </PageContainer>
  );
}
